import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Loader2 } from "lucide-react";
import {
  API,
  BUSINESS,
  ROOF_OPTIONS,
  HOUSE_OPTIONS,
} from "@/lib/config";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    zip_code: "",
    roof_type: "shingle",
    house_type: "one_story",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !/^\d{5}$/.test(form.zip_code)) {
      toast.error("Please fill in name, email, phone, and a 5-digit zip.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Got it! We'll reach out within 1 business hour.");
      setForm({
        name: "",
        email: "",
        phone: "",
        zip_code: "",
        roof_type: "shingle",
        house_type: "one_story",
        notes: "",
      });
    } catch {
      toast.error("Could not submit. Try calling us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left — info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="bid-divider" />
              <span className="bid-eyebrow">Talk to us</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight mb-6">
              Prefer a real
              <br />
              human first?
            </h2>
            <p className="text-slate-700 leading-relaxed mb-10">
              Tap the number below or send us a quick callback request — we
              typically respond within one business hour during business hours.
            </p>

            <ul className="space-y-5">
              <Info icon={Phone} label="Phone" value={BUSINESS.phone} href={`tel:${BUSINESS.phoneRaw}`} testid="contact-phone" />
              <Info icon={Mail} label="Email" value={BUSINESS.email} href={`mailto:${BUSINESS.email}`} testid="contact-email" />
              <Info icon={MapPin} label="Service area" value={BUSINESS.address} testid="contact-address" />
              <li>
                <div className="flex items-start gap-4" data-testid="contact-hours">
                  <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center shrink-0">
                    <Clock size={16} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">
                      Hours
                    </div>
                    <ul className="space-y-1">
                      {BUSINESS.hoursDetailed.map((d) => (
                        <li
                          key={d.day}
                          className="flex justify-between text-sm font-mono text-slate-700 gap-6"
                        >
                          <span className="font-semibold text-slate-900">
                            {d.day}
                          </span>
                          <span
                            className={
                              d.hours === "Closed"
                                ? "text-[#FF2A2A] font-semibold"
                                : ""
                            }
                          >
                            {d.hours}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            </ul>

            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="bid-btn-danger mt-10"
              data-testid="contact-book-square-btn"
            >
              <Phone size={16} />
              Call {BUSINESS.phone}
            </a>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="border-2 border-slate-900 bg-white p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
              data-testid="booking-form"
            >
              <Field label="Full name" required>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="bid-input"
                  data-testid="form-name"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="bid-input"
                  data-testid="form-email"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className="bid-input"
                  data-testid="form-phone"
                />
              </Field>
              <Field label="Zip code" required>
                <input
                  required
                  inputMode="numeric"
                  maxLength={5}
                  value={form.zip_code}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
                    })
                  }
                  className="bid-input font-mono tracking-widest"
                  data-testid="form-zip"
                />
              </Field>
              <Field label="Roof type">
                <select
                  value={form.roof_type}
                  onChange={update("roof_type")}
                  className="bid-input"
                  data-testid="form-roof"
                >
                  {ROOF_OPTIONS.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Home type">
                <select
                  value={form.house_type}
                  onChange={update("house_type")}
                  className="bid-input"
                  data-testid="form-house"
                >
                  {HOUSE_OPTIONS.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Notes (optional)">
                  <textarea
                    rows={4}
                    value={form.notes}
                    onChange={update("notes")}
                    className="bid-input resize-none"
                    placeholder="Anything we should know? Pets, gate codes, scheduling preferences..."
                    data-testid="form-notes"
                  />
                </Field>
              </div>

              <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
                  We never share your info.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bid-btn-primary disabled:opacity-60"
                  data-testid="form-submit"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : null}
                  Request callback
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .bid-input {
          width: 100%;
          padding: 0.85rem 1rem;
          background: #fff;
          border: 2px solid #e2e8f0;
          color: #0a0a0a;
          font-family: 'Manrope', sans-serif;
          font-size: 1rem;
          transition: border-color 160ms ease;
        }
        .bid-input:focus {
          outline: none;
          border-color: #002FA7;
        }
      `}</style>
    </section>
  );
}

function Info({ icon: Icon, label, value, href, testid }) {
  const Wrap = href ? "a" : "div";
  return (
    <li>
      <Wrap
        href={href}
        data-testid={testid}
        className="flex items-start gap-4 group"
      >
        <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center shrink-0 group-hover:bg-[#002FA7] transition-colors">
          <Icon size={16} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">
            {label}
          </div>
          <div className="text-base text-slate-900 font-semibold">{value}</div>
        </div>
      </Wrap>
    </li>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-slate-700 font-bold block mb-2">
        {label}
        {required && <span className="text-[#FF2A2A]"> *</span>}
      </span>
      {children}
    </label>
  );
}
